import { sql } from "drizzle-orm";
import { integer, real, sqliteTable, text, uniqueIndex, index, check, type AnySQLiteColumn } from "drizzle-orm/sqlite-core";
import { key, timestamps } from "./columns";
import {
  ARTICLE_AUDIENCES,
  ARTICLE_KINDS,
  ARTICLE_PLANS,
  ARTICLE_STATES,
  AUDIT_TARGET_KINDS,
  BRAND_IDS,
  CONTENT_ORIGINS,
  CONTENT_SOURCES,
  CSAT_SOURCES,
  HEALTH,
  KB_STATUSES,
  KB_TYPES,
  MEDIA_KINDS,
  MEDIA_STATES,
  MESSAGE_DIRECTIONS,
  PROJECT_MEMBER_ROLES,
  PROPOSAL_KINDS,
  PROPOSAL_RESOLUTIONS,
  PROPOSAL_STATES,
  REALTY_ID_VERIFY_VIA,
  TENANT_STATUSES,
  TICKET_CHANNELS,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  TIME_ENTRY_SOURCES,
  TONES,
} from "./enums";

/* ------------------------------------------------------------------ *
 * Tenant (client MLS) — PK is `mls_id`, matching recursive-mls.
 * ------------------------------------------------------------------ */

export const tenant = sqliteTable("tenant", {
  id: text("mls_id").primaryKey(), // slug, e.g. "miami"
  name: text("name").notNull(),
  host: text("host").notNull(),
  env: text("env").notNull().default("Production"),
  plan: text("plan").notNull().default("Standard"),
  users: integer("users").notNull().default(0),
  timezone: text("timezone").notNull().default("America/New_York"),
  status: text("status", { enum: TENANT_STATUSES }).notNull().default("active"),
  provider: text("provider").notNull().default("displet"),
  health: text("health", { enum: HEALTH }).notNull().default("ok"),
  dataResidency: text("data_residency").notNull().default("us"),
  legalHold: integer("legal_hold", { mode: "boolean" }).notNull().default(false),
  ...timestamps,
});

export const tenantBusinessHours = sqliteTable("tenant_business_hours", {
  id: key("hours_key", "tbh"),
  mlsId: text("mls_id")
    .notNull()
    .references(() => tenant.id),
  weekday: integer("weekday").notNull(),
  openMinute: integer("open_minute"),
  closeMinute: integer("close_minute"),
  ...timestamps,
});

export const holiday = sqliteTable("holiday", {
  id: key("holiday_key", "hol"),
  mlsId: text("mls_id").references(() => tenant.id),
  name: text("name").notNull(),
  date: text("date").notNull(),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Staff, roles, capabilities, mailboxes, signatures
 * ------------------------------------------------------------------ */

export const staffUser = sqliteTable(
  "staff_user",
  {
    id: key("staff_key", "stf"),
    name: text("name").notNull(),
    email: text("email").notNull(),
    initials: text("initials").notNull(),
    title: text("title").notNull().default("Support · TRIBUS"),
    roleId: text("role_key").references(() => role.id),
    status: text("status").notNull().default("offline"),
    isAdmin: integer("is_admin", { mode: "boolean" }).notNull().default(false),
    ...timestamps,
  },
  // One staff row per email — the identity key for Access JIT provisioning.
  (t) => ({ uniqEmail: uniqueIndex("idx_staff_user_email").on(t.email) }),
);

export const role = sqliteTable("role", {
  id: key("role_key", "rol"),
  slug: text("slug").notNull(),
  name: text("name").notNull(),
  ...timestamps,
});

export const roleCapability = sqliteTable(
  "role_capability",
  {
    id: key("capability_key", "rlc"),
    roleId: text("role_key")
      .notNull()
      .references(() => role.id),
    capability: text("capability").notNull(),
    allowed: integer("allowed", { mode: "boolean" }).notNull().default(true),
    ...timestamps,
  },
  (t) => ({ uniq: uniqueIndex("idx_role_capability_uniq").on(t.roleId, t.capability) }),
);

export const mailbox = sqliteTable(
  "mailbox",
  {
    id: key("mailbox_key", "mbx"),
    address: text("address").notNull(),
    label: text("label").notNull(),
    signoff: text("signoff").notNull().default("Recursive Support"),
    color: text("color").notNull().default("#006747"),
    // Outbound identity for replies from this mailbox (§4.5). fromName is the
    // display name recipients see. replyTo holds the REAL customer-facing address
    // (e.g. support@solidearth.com) — distinct from `address`, which is the mls.dev
    // routing address the mail physically arrives on. It is the address we send AS
    // (see resolveSender in api/email/identity.ts: used as From when on an
    // authorized sending domain) AND the reply destination; the two coincide in the
    // current model. Both optional.
    fromName: text("from_name"),
    replyTo: text("reply_to"),
    ...timestamps,
  },
  // The inbound routing key must be unique — two rows would split a client's mail.
  (t) => ({ uniqAddress: uniqueIndex("idx_mailbox_address").on(t.address) }),
);

export const mailboxMember = sqliteTable(
  "mailbox_member",
  {
    id: key("member_key", "mbm"),
    mailboxId: text("mailbox_key")
      .notNull()
      .references(() => mailbox.id),
    staffUserId: text("staff_key")
      .notNull()
      .references(() => staffUser.id),
    ...timestamps,
  },
  (t) => ({ uniq: uniqueIndex("idx_mailbox_member_uniq").on(t.mailboxId, t.staffUserId) }),
);

export const signature = sqliteTable(
  "signature_block",
  {
    id: key("sig_key", "sig"),
    staffUserId: text("staff_key")
      .notNull()
      .references(() => staffUser.id),
    mailboxId: text("mailbox_key")
      .notNull()
      .references(() => mailbox.id),
    body: text("body").notNull(),
    ...timestamps,
  },
  (t) => ({ uniq: uniqueIndex("idx_signature_block_staff").on(t.staffUserId, t.mailboxId) }),
);

/* ------------------------------------------------------------------ *
 * People (requesters) & cached identity snapshots
 * ------------------------------------------------------------------ */

export const person = sqliteTable(
  "person",
  {
    id: key("person_key", "per"),
    mlsId: text("mls_id")
      .notNull()
      .references(() => tenant.id),
    name: text("name").notNull(),
    initials: text("initials").notNull(),
    email: text("email"),
    phone: text("phone"),
    role: text("role"),
    memberKey: text("member_key"),
    realtyId: text("realty_id"),
    ...timestamps,
  },
  (t) => ({ byTenant: index("idx_person_mls").on(t.mlsId) }),
);

export const personCache = sqliteTable("person_cache", {
  id: key("cache_key", "pcc"),
  personId: text("person_key")
    .notNull()
    .references(() => person.id),
  source: text("source").notNull(),
  snapshot: text("snapshot", { mode: "json" }).$type<Record<string, unknown>>(),
  fetchedAt: text("fetched_at"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Tickets, messages, events, timers, tiering
 * ------------------------------------------------------------------ */

export const ticket = sqliteTable(
  "ticket",
  {
    id: key("ticket_key", "tck"), // display ref TCK-#### derived via ticketRef()
    // Nullable: a ticket may have NO client (external senders — integration
    // partners, unknown people, spam). Known senders are still attributed to a
    // tenant; unknown/external ones get a ticket with mls_id = null (flagged).
    mlsId: text("mls_id").references(() => tenant.id),
    mailboxId: text("mailbox_key").references(() => mailbox.id),
    channel: text("channel", { enum: TICKET_CHANNELS }).notNull().default("form"),
    status: text("status", { enum: TICKET_STATUSES }).notNull().default("new"),
    priority: text("priority", { enum: TICKET_PRIORITIES }).notNull().default("normal"),
    subject: text("subject"),
    preview: text("preview"),
    requesterKey: text("requester_key").references(() => person.id),
    requesterName: text("requester_name"),
    requesterEmail: text("requester_email"),
    requesterPhone: text("requester_phone"),
    // Canonical client id (= Displet network.uid) the requester belongs to,
    // stamped at ingestion so per-client metrics are a cheap group-by (no
    // per-request cross-DB lookup).
    networkUuid: text("network_uuid"),
    // The requester's Displet user uid, resolved from their email/phone against
    // the Displet roster at ingest (the same resolver telephony uses). Lets the
    // rail fetch /people/:uid for the sender's live RealtyID + member record when
    // they're a known Displet user but not a seeded local person. Null = unknown /
    // external sender.
    requesterUid: text("requester_uid"),
    assigneeKey: text("assignee_key"),
    assigneeName: text("assignee_name"),
    relatedRecord: text("related_record", { mode: "json" }).$type<Record<string, unknown> | null>(),
    tags: text("tags", { mode: "json" }).$type<string[]>().default([]),
    screenContext: text("screen_context"),
    emailThreadId: text("email_thread_id"),
    firstResponseAt: text("first_response_at"),
    solvedAt: text("solved_at"),
    // staff-platform extensions
    tier: integer("tier").notNull().default(1),
    summary: text("summary"),
    projectId: text("project_key"),
    language: text("language"),
    live: integer("live", { mode: "boolean" }).notNull().default(false),
    outOfScope: integer("out_of_scope", { mode: "boolean" }).notNull().default(false),
    sentiment: text("sentiment"),
    sentimentTone: text("sentiment_tone", { enum: TONES }),
    slaLabel: text("sla_label"),
    slaTone: text("sla_tone", { enum: TONES }),
    slaDueAt: text("sla_due_at"),
    holdUntil: text("hold_until"),
    receivedAt: text("received_at").notNull(),
    // ISO timestamp of when the ticket was moved to a hidden status (spam/deleted).
    // Set on the transition into spam/deleted, cleared (null) on restore. Drives
    // the 30-day retention purge — NOT updatedAt, which any write bumps.
    hiddenAt: text("hidden_at"),
    // Max inbound-message threat level (none|low|medium|high|critical), denormalized
    // from ticket_message risk so the "Flagged" queue is an indexed lookup instead
    // of a json_extract scan. Set at ingest / AI-enrichment; only ever raised.
    riskLevel: text("risk_level"),
    survey: text("survey", { mode: "json" }).$type<Record<string, unknown> | null>(),
    // Provenance for records imported from an external system (HubSpot). external_id
    // is that system's ticket id; the (source, id) pair is unique so a re-run upserts
    // instead of duplicating. external_updated_at (their last-modified) drives the
    // delta-sync stale-guard. All null on native (email/SMS/seed) tickets.
    externalSource: text("external_source"),
    externalId: text("external_id"),
    externalUpdatedAt: text("external_updated_at"),
    // First-class brand (the HubSpot pipeline). Brands (solidearth|tribus|nautilent|
    // accounting) are NOT tenants, so this is a plain indexed label rather than an
    // mls_id. Null when not brand-attributed.
    brand: text("brand"),
    // Inert archival record: a mirror of a ticket owned elsewhere (an imported HubSpot
    // ticket, or — later — a mirror-mode inbound copy). Excluded from the SLA sweep and
    // open queues, and the reply endpoint refuses to send from it, so importing history
    // can never message a customer. Only ever true for such rows.
    archival: integer("archival", { mode: "boolean" }).notNull().default(false),
    ...timestamps,
  },
  (t) => ({
    byStatus: index("idx_ticket_mls_status").on(t.mlsId, t.status, t.updatedAt),
    byRequester: index("idx_ticket_requester").on(t.requesterKey),
    byNetwork: index("idx_ticket_network").on(t.networkUuid, t.status),
    byMailbox: index("idx_ticket_inbox").on(t.mailboxId),
    byReceived: index("idx_ticket_received").on(t.receivedAt),
    byEmailThread: index("idx_ticket_email_thread").on(t.emailThreadId),
    // Purge sweep: find hidden tickets past their retention window.
    byHidden: index("idx_ticket_hidden").on(t.status, t.hiddenAt),
    // "Flagged" queue: indexed lookup of threat-scored tickets.
    byRisk: index("idx_ticket_risk").on(t.riskLevel),
    // Idempotent import key. Partial (both columns non-null) so uniqueness covers
    // exactly the rows that carry an external id — native tickets (external_id null)
    // are excluded from the index, and a half-keyed row can't silently dodge dedup.
    byExternal: uniqueIndex("idx_ticket_external")
      .on(t.externalSource, t.externalId)
      .where(sql`external_source is not null and external_id is not null`),
    // Brand rollups without a json_extract scan.
    byBrand: index("idx_ticket_brand").on(t.brand),
    // Both-or-neither: a row can't claim an external source without an id (or vice
    // versa), so a half-keyed ('hubspot', NULL) row can't slip past the partial
    // unique index and silently duplicate on re-import.
    externalBothOrNeither: check(
      "chk_ticket_external_both_or_neither",
      sql`(external_source is null) = (external_id is null)`,
    ),
  }),
);

export const ticketMessage = sqliteTable(
  "ticket_message",
  {
    id: key("message_key", "tms"),
    ticketId: text("ticket_key")
      .notNull()
      .references(() => ticket.id),
    mlsId: text("mls_id"), // nullable — mirrors ticket.mls_id (external/no-client tickets)
    direction: text("direction", { enum: MESSAGE_DIRECTIONS }).notNull().default("inbound"),
    channel: text("channel", { enum: TICKET_CHANNELS }).notNull().default("form"),
    authorKey: text("author_key"),
    authorName: text("author_name"),
    body: text("body"),
    // staff-platform translation extension
    translatedBody: text("translated_body"),
    lang: text("lang"),
    displayAt: text("display_at"), // prototype label, e.g. "Today · 9:14 AM"
    emailMeta: text("email_meta", { mode: "json" }).$type<Record<string, unknown>>(),
    voiceMeta: text("voice_meta", { mode: "json" }).$type<Record<string, unknown>>(),
    smsMeta: text("sms_meta", { mode: "json" }).$type<Record<string, unknown>>(),
    attachments: text("attachments", { mode: "json" }).$type<unknown[]>().default([]),
    kbArticleRefs: text("kb_article_refs", { mode: "json" }).$type<string[]>().default([]),
    // Outbound delivery lifecycle (email). Set at send time (sent|failed) and later
    // reconciled from Cloudflare Email Sending events (delivered|deferred|bounced|
    // rejected|complained). Null on inbound + non-email messages.
    deliveryStatus: text("delivery_status"),
    deliveryDetail: text("delivery_detail"), // SMTP response / bounce reason / send error
    // The provider's message id (Cloudflare Email Sending), used to correlate an
    // inbound delivery event back to this row. Indexed for that lookup.
    providerMessageId: text("provider_message_id"),
    // Provenance for imported messages (the HubSpot conversation-message id); the
    // (source, id) pair is unique so re-imports upsert. Null on native messages.
    externalSource: text("external_source"),
    externalId: text("external_id"),
    createdAt: timestamps.createdAt, // messages are immutable — no updated_at
  },
  (t) => ({
    byTicket: index("idx_ticket_message_ticket").on(t.ticketId, t.createdAt),
    byProvider: index("idx_ticket_message_provider").on(t.providerMessageId),
    // Idempotent per-message import key (partial — see idx_ticket_external).
    byExternal: uniqueIndex("idx_ticket_message_external")
      .on(t.externalSource, t.externalId)
      .where(sql`external_source is not null and external_id is not null`),
    externalBothOrNeither: check(
      "chk_ticket_message_external_both_or_neither",
      sql`(external_source is null) = (external_id is null)`,
    ),
  }),
);

export const ticketEvent = sqliteTable(
  "ticket_event",
  {
    id: key("event_key", "tev"),
    ticketId: text("ticket_key")
      .notNull()
      .references(() => ticket.id),
    kind: text("kind").notNull(),
    actor: text("actor"),
    detail: text("detail", { mode: "json" }).$type<Record<string, unknown>>(),
    ...timestamps,
  },
  (t) => ({ byTicket: index("idx_ticket_event_ticket").on(t.ticketId) }),
);

export const ticketTimer = sqliteTable("ticket_timer", {
  id: key("timer_key", "tmr"),
  ticketId: text("ticket_key")
    .notNull()
    .references(() => ticket.id),
  staffUserId: text("staff_key")
    .notNull()
    .references(() => staffUser.id),
  startedAt: text("started_at"),
  rawSeconds: integer("raw_seconds").notNull().default(0),
  minutes: integer("minutes").notNull().default(0),
  billable: integer("billable", { mode: "boolean" }).notNull().default(false),
  note: text("note"),
  ...timestamps,
});

export const offerLog = sqliteTable("offer_log", {
  id: key("offer_key", "ofr"),
  ticketId: text("ticket_key")
    .notNull()
    .references(() => ticket.id),
  tier: integer("tier").notNull().default(1),
  staffUserId: text("staff_key").references(() => staffUser.id),
  repName: text("rep_name"),
  outcome: text("outcome").notNull(),
  outcomeTone: text("outcome_tone", { enum: TONES }),
  detail: text("detail"),
  offeredAt: text("offered_at"),
  ...timestamps,
});

export const nudgeLog = sqliteTable("nudge_log", {
  id: key("nudge_key", "ndg"),
  ticketId: text("ticket_key")
    .notNull()
    .references(() => ticket.id),
  staffUserId: text("staff_key").references(() => staffUser.id),
  rule: text("rule").notNull(),
  exit: text("exit"),
  reason: text("reason"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * CSAT, chat, calls, sms, verify
 * ------------------------------------------------------------------ */

export const csatResponse = sqliteTable("csat_response", {
  id: key("csat_key", "cst"),
  mlsId: text("mls_id").references(() => tenant.id),
  ticketId: text("ticket_key").references(() => ticket.id),
  source: text("source", { enum: CSAT_SOURCES }).notNull(),
  score: integer("score").notNull(),
  comment: text("comment"),
  staffUserId: text("staff_key").references(() => staffUser.id),
  ...timestamps,
});

export const chatFlow = sqliteTable("chat_flow", {
  id: key("flow_key", "cfl"),
  mlsId: text("mls_id").references(() => tenant.id),
  name: text("name").notNull(),
  steps: text("steps", { mode: "json" }).$type<unknown[]>(),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  ...timestamps,
});

export const chatSession = sqliteTable("chat_session", {
  id: key("session_key", "chs"),
  mlsId: text("mls_id").references(() => tenant.id),
  ticketId: text("ticket_key").references(() => ticket.id),
  visitor: text("visitor"),
  visitorInitials: text("visitor_initials"),
  role: text("role"),
  page: text("page"),
  browser: text("browser"),
  status: text("status").notNull().default("waiting"),
  waitLabel: text("wait_label"),
  unread: integer("unread").notNull().default(0),
  messages: text("messages", { mode: "json" }).$type<unknown[]>(),
  ...timestamps,
});

export const chatFlowRun = sqliteTable("chat_flow_run", {
  id: key("run_key", "cfr"),
  chatFlowId: text("flow_key").references(() => chatFlow.id),
  chatSessionId: text("session_key").references(() => chatSession.id),
  transcript: text("transcript", { mode: "json" }).$type<unknown[]>(),
  ...timestamps,
});

export const call = sqliteTable("call", {
  id: key("call_key", "cll"),
  mlsId: text("mls_id").references(() => tenant.id),
  ticketId: text("ticket_key").references(() => ticket.id),
  direction: text("direction").notNull(),
  who: text("who"),
  number: text("number"),
  whenLabel: text("when_label"),
  durationLabel: text("duration_label"),
  outcome: text("outcome"),
  handledBy: text("handled_by"),
  recordingR2Key: text("recording_r2_key"),
  transcript: text("transcript", { mode: "json" }).$type<unknown[]>(),
  ...timestamps,
});

export const smsThread = sqliteTable("sms_thread", {
  id: key("sms_key", "sms"),
  mlsId: text("mls_id").references(() => tenant.id),
  ticketId: text("ticket_key").references(() => ticket.id),
  who: text("who"),
  number: text("number"),
  lastMessage: text("last_message"),
  whenLabel: text("when_label"),
  ...timestamps,
});

export const verifyCode = sqliteTable("verify_code", {
  id: key("verify_key", "vfy"),
  personId: text("person_key").references(() => person.id),
  ticketId: text("ticket_key").references(() => ticket.id),
  codeHash: text("code_hash").notNull(),
  expiresAt: text("expires_at").notNull(),
  verifiedAt: text("verified_at"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Knowledge, macros, bulk sends
 * ------------------------------------------------------------------ */

export const kbItem = sqliteTable(
  "kb_item",
  {
    id: key("item_key", "kbi"),
    mlsId: text("mls_id").references(() => tenant.id), // null = platform-wide
    folderKey: text("folder_key"),
    type: text("type", { enum: KB_TYPES }).notNull().default("article"),
    title: text("title").notNull(),
    subtitle: text("subtitle"),
    body: text("body"),
    mediaKey: text("media_key"),
    mediaUrl: text("media_url"),
    tags: text("tags", { mode: "json" }).$type<string[]>().default([]),
    status: text("status", { enum: KB_STATUSES }).notNull().default("published"),
    views: integer("views").notNull().default(0),
    featured: integer("featured", { mode: "boolean" }).notNull().default(false),
    version: integer("version").notNull().default(1),
    // staff-platform display extensions
    scopeLabel: text("scope_label"),
    updatedLabel: text("updated_label"),
    ...timestamps,
  },
  (t) => ({
    byStatus: index("idx_kb_item_status").on(t.mlsId, t.status),
    byType: index("idx_kb_item_type").on(t.mlsId, t.type),
  }),
);

export const kbGap = sqliteTable("kb_gap", {
  id: key("gap_key", "kbg"),
  topic: text("topic").notNull(),
  count: integer("count").notNull().default(0),
  tenants: text("tenants"),
  lastLabel: text("last_label"),
  draftTitle: text("draft_title"),
  draftBody: text("draft_body"),
  ...timestamps,
});

export const macro = sqliteTable(
  "macro",
  {
    id: key("macro_key", "mac"),
    mlsId: text("mls_id").references(() => tenant.id), // null = platform-wide
    title: text("title").notNull(),
    body: text("body"),
    tags: text("tags", { mode: "json" }).$type<string[]>().default([]),
    category: text("category"), // staff-platform extension
    uses: integer("uses").notNull().default(0),
    createdBy: text("created_by"),
    ...timestamps,
  },
  (t) => ({ byTenant: index("idx_macro_mls").on(t.mlsId) }),
);

export const bulkSend = sqliteTable("bulk_send", {
  id: key("bulk_key", "blk"),
  macroId: text("macro_key").references(() => macro.id),
  recipients: integer("recipients").notNull().default(0),
  requestedBy: text("requested_by").references(() => staffUser.id),
  approvedBy: text("approved_by").references(() => staffUser.id),
  status: text("status").notNull().default("pending"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Announcements, changelog, roadmap
 * ------------------------------------------------------------------ */

export const announcement = sqliteTable("announcement", {
  id: key("announcement_key", "ann"),
  mlsId: text("mls_id").references(() => tenant.id),
  title: text("title").notNull(),
  body: text("body"),
  channels: text("channels", { mode: "json" }).$type<string[]>(),
  scheduledAt: text("scheduled_at"),
  approvedBy: text("approved_by").references(() => staffUser.id),
  ...timestamps,
});

export const announcementReceipt = sqliteTable("announcement_receipt", {
  id: key("receipt_key", "arc"),
  announcementId: text("announcement_key")
    .notNull()
    .references(() => announcement.id),
  mlsId: text("mls_id").references(() => tenant.id),
  readAt: text("read_at"),
  ...timestamps,
});

export const changelogRelease = sqliteTable("changelog_release", {
  id: key("release_key", "rel"),
  version: text("version").notNull(),
  publishedAt: text("published_at"),
  adminNote: text("admin_note"),
  ...timestamps,
});

export const changelogItem = sqliteTable("changelog_item", {
  id: key("changelog_key", "cli"),
  releaseId: text("release_key").references(() => changelogRelease.id),
  kind: text("kind").notNull().default("New"),
  surveyKey: text("survey_key"),
  reportedByMlsId: text("reported_by_mls_id").references(() => tenant.id),
  title: text("title").notNull(),
  body: text("body"),
  mediaKey: text("media_key"),
  mediaType: text("media_type"),
  duration: text("duration"),
  ...timestamps,
});

export const roadmapItem = sqliteTable("roadmap_item", {
  id: key("roadmap_key", "rdm"),
  bucket: text("bucket").notNull().default("later"),
  title: text("title").notNull(),
  body: text("body"),
  votes: integer("votes").notNull().default(0),
  ...timestamps,
});

export const roadmapVote = sqliteTable(
  "roadmap_vote",
  {
    id: key("vote_key", "rdv"),
    roadmapItemId: text("roadmap_key")
      .notNull()
      .references(() => roadmapItem.id),
    ticketId: text("ticket_key").references(() => ticket.id),
    mlsId: text("mls_id").references(() => tenant.id),
    // Who cast the vote (staff_user id). Nullable so seeded/ticket-derived votes
    // don't need one; the unique index below dedupes real per-user votes.
    voterKey: text("voter_key"),
    ...timestamps,
  },
  (t) => ({ uniqVoter: uniqueIndex("idx_roadmap_vote_voter").on(t.roadmapItemId, t.voterKey) }),
);

/* ------------------------------------------------------------------ *
 * Incidents & status pages
 * ------------------------------------------------------------------ */

export const incident = sqliteTable("incident", {
  id: key("incident_key", "inc"),
  mlsId: text("mls_id").references(() => tenant.id),
  title: text("title").notNull(),
  severity: text("severity").notNull().default("minor"),
  status: text("status").notNull().default("investigating"),
  ...timestamps,
});

export const incidentUpdate = sqliteTable("incident_update", {
  id: key("update_key", "inu"),
  incidentId: text("incident_key")
    .notNull()
    .references(() => incident.id),
  body: text("body").notNull(),
  status: text("status"),
  ...timestamps,
});

export const statusSubscriber = sqliteTable("status_subscriber", {
  id: key("subscriber_key", "sub"),
  mlsId: text("mls_id").references(() => tenant.id),
  contact: text("contact").notNull(),
  channel: text("channel").notNull().default("email"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Projects, tasks, time, billing
 * ------------------------------------------------------------------ */

export const project = sqliteTable("project", {
  id: key("project_key", "prj"),
  mlsId: text("mls_id")
    .notNull()
    .references(() => tenant.id),
  name: text("name").notNull(),
  status: text("status").notNull().default("active"),
  pm: text("pm"),
  scheduleHealth: text("schedule_health", { enum: TONES }).notNull().default("ok"),
  progressPct: integer("progress_pct").notNull().default(0),
  goLiveDate: text("go_live_date"),
  clientContacts: text("client_contacts", { mode: "json" }).$type<string[]>(),
  // v3 (DATA_MODEL.md §4): approved hours drive the portal burn-down; phase and
  // start date frame the timeline.
  approvedHours: real("approved_hours").notNull().default(0),
  phase: text("phase"),
  startedOn: text("started_on"),
  ...timestamps,
});

// Project membership (DATA_MODEL.md §4) — the ONE access control for client-side
// project visibility (DECISIONS.md §5: a client user sees a project iff a row
// exists here; there is no global toggle). Exactly one of admin_id (our side) /
// user_id (client side) is set.
export const projectMember = sqliteTable(
  "project_member",
  {
    id: key("member_key", "pmb"),
    projectId: text("project_key")
      .notNull()
      .references(() => project.id),
    adminId: text("admin_key").references(() => staffUser.id), // our side
    userId: text("user_key").references(() => person.id), // client side
    role: text("role", { enum: PROJECT_MEMBER_ROLES }).notNull(),
    addedBy: text("added_by")
      .notNull()
      .references(() => staffUser.id),
    addedAt: text("added_at").notNull(),
    ...timestamps,
  },
  (t) => ({
    byProject: index("idx_project_member_project").on(t.projectId),
    // No duplicate membership rows per side (partial: NULLs on the other side
    // don't collide).
    uniqUser: uniqueIndex("idx_project_member_user").on(t.projectId, t.userId).where(sql`user_key is not null`),
    uniqAdmin: uniqueIndex("idx_project_member_admin").on(t.projectId, t.adminId).where(sql`admin_key is not null`),
    // Exactly one side set (DATA_MODEL.md §4 CHECK).
    oneSide: check("chk_project_member_one_side", sql`(admin_key is null) <> (user_key is null)`),
  }),
);

export const projectList = sqliteTable("project_list", {
  id: key("list_key", "prl"),
  projectId: text("project_key")
    .notNull()
    .references(() => project.id),
  name: text("name").notNull(),
  position: integer("position").notNull().default(0),
  ...timestamps,
});

export const task = sqliteTable("task", {
  id: key("task_key", "tsk"),
  projectId: text("project_key")
    .notNull()
    .references(() => project.id),
  listId: text("list_key").references(() => projectList.id),
  title: text("title").notNull(),
  status: text("status").notNull().default("todo"),
  assignee: text("assignee"),
  dueDate: text("due_date"),
  points: integer("points"),
  blockedSince: text("blocked_since"),
  position: integer("position").notNull().default(0),
  ...timestamps,
});

export const taskTemplate = sqliteTable("task_template", {
  id: key("template_key", "tpl"),
  name: text("name").notNull(),
  tasks: text("tasks", { mode: "json" }).$type<unknown[]>(),
  ...timestamps,
});

export const rateCard = sqliteTable(
  "rate_card",
  {
    id: key("rate_key", "rat"),
    projectId: text("project_key")
      .notNull()
      .references(() => project.id),
    staffUserId: text("staff_key")
      .notNull()
      .references(() => staffUser.id),
    hourlyRate: real("hourly_rate").notNull(),
    ...timestamps,
  },
  (t) => ({ uniq: uniqueIndex("idx_rate_card_uniq").on(t.projectId, t.staffUserId) }),
);

export const timeEntry = sqliteTable("time_entry", {
  id: key("entry_key", "tme"),
  projectId: text("project_key").references(() => project.id),
  ticketId: text("ticket_key").references(() => ticket.id),
  staffUserId: text("staff_key")
    .notNull()
    .references(() => staffUser.id),
  rawSeconds: integer("raw_seconds").notNull().default(0),
  minutes: integer("minutes").notNull().default(0),
  billable: integer("billable", { mode: "boolean" }).notNull().default(false),
  billed: integer("billed", { mode: "boolean" }).notNull().default(false),
  note: text("note"),
  source: text("source", { enum: TIME_ENTRY_SOURCES }).notNull().default("manual"),
  // v3 (DATA_MODEL.md §4): internal entries exist; the portal time audit filters
  // on this so a client never sees internal time.
  clientVisible: integer("client_visible", { mode: "boolean" }).notNull().default(true),
  ...timestamps,
}, (t) => ({
  // Scoped, ordered reads of a ticket's time entries (the ticket time rail).
  byTicket: index("idx_time_entry_ticket").on(t.ticketId, t.createdAt),
}));

export const invoice = sqliteTable("invoice", {
  id: key("invoice_key", "inv"),
  projectId: text("project_key")
    .notNull()
    .references(() => project.id),
  mlsId: text("mls_id")
    .notNull()
    .references(() => tenant.id),
  billcomInvoiceId: text("billcom_invoice_id"),
  status: text("status").notNull().default("draft"),
  totalCents: integer("total_cents").notNull().default(0),
  terms: text("terms").notNull().default("Net 30"),
  ...timestamps,
});

export const invoiceLine = sqliteTable("invoice_line", {
  id: key("line_key", "ivl"),
  invoiceId: text("invoice_key")
    .notNull()
    .references(() => invoice.id),
  staffUserId: text("staff_key").references(() => staffUser.id),
  description: text("description"),
  hours: real("hours").notNull().default(0),
  rate: real("rate").notNull().default(0),
  amountCents: integer("amount_cents").notNull().default(0),
  ...timestamps,
});

export const projectMessage = sqliteTable("project_message", {
  id: key("pmessage_key", "pms"),
  projectId: text("project_key")
    .notNull()
    .references(() => project.id),
  subject: text("subject"),
  body: text("body"),
  aiDrafted: integer("ai_drafted", { mode: "boolean" }).notNull().default(false),
  approvedBy: text("approved_by").references(() => staffUser.id),
  sentAt: text("sent_at"),
  ...timestamps,
});

export const projectWarning = sqliteTable("project_warning", {
  id: key("warning_key", "pwn"),
  projectId: text("project_key")
    .notNull()
    .references(() => project.id),
  level: text("level").notNull().default("watch"),
  rule: text("rule").notNull(),
  message: text("message").notNull(),
  ...timestamps,
});

export const subscription = sqliteTable("subscription", {
  id: key("subscription_key", "sbn"),
  projectId: text("project_key")
    .notNull()
    .references(() => project.id),
  startDate: text("start_date"),
  plan: text("plan"),
  arrCents: integer("arr_cents"),
  terms: text("terms"),
  hypercareEnd: text("hypercare_end"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Team, tiers, schedule, emergency line
 * ------------------------------------------------------------------ */

export const rep = sqliteTable("rep", {
  id: key("rep_key", "rep"),
  staffUserId: text("staff_key").references(() => staffUser.id),
  name: text("name").notNull(),
  initials: text("initials").notNull(),
  status: text("status").notNull().default("offline"),
  offered: integer("offered").notNull().default(0),
  accepted: integer("accepted").notNull().default(0),
  solved: integer("solved").notNull().default(0),
  firstReply: text("first_reply"),
  handle: text("handle"),
  slaMet: integer("sla_met").notNull().default(0),
  csat: text("csat"),
  reopens: integer("reopens").notNull().default(0),
  active: text("active"),
  timedOut: integer("timed_out").notNull().default(0),
  declined: integer("declined").notNull().default(0),
  ...timestamps,
});

export const tier = sqliteTable("tier", {
  id: key("tier_key", "tir"),
  level: integer("level").notNull(),
  name: text("name").notNull(),
  coverage: text("coverage"),
  ...timestamps,
});

export const shift = sqliteTable("shift", {
  id: key("shift_key", "shf"),
  tierId: text("tier_key").references(() => tier.id),
  staffUserId: text("staff_key").references(() => staffUser.id),
  startsAt: text("starts_at"),
  endsAt: text("ends_at"),
  ...timestamps,
});

export const emergencyRingStep = sqliteTable("emergency_ring_step", {
  id: key("step_key", "ers"),
  position: integer("position").notNull(),
  target: text("target").notNull(),
  ringSeconds: integer("ring_seconds").notNull().default(60),
  ...timestamps,
});

export const emergencyContactLog = sqliteTable("emergency_contact_log", {
  id: key("contact_key", "ecl"),
  ticketId: text("ticket_key").references(() => ticket.id),
  number: text("number"),
  outcome: text("outcome"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Security: sign-ins, devices, breaches
 * ------------------------------------------------------------------ */

export const signinEvent = sqliteTable("signin_event", {
  id: key("signin_key", "sgn"),
  personId: text("person_key").references(() => person.id),
  mlsId: text("mls_id").references(() => tenant.id),
  app: text("app"),
  ip: text("ip"),
  lat: real("lat"),
  lon: real("lon"),
  deviceId: text("device_id"),
  method: text("method"),
  result: text("result"),
  botScore: integer("bot_score"),
  wafAction: text("waf_action"),
  asn: text("asn"),
  at: text("at"),
  ...timestamps,
});

export const device = sqliteTable("device", {
  id: key("device_key", "dev"),
  personId: text("person_key").references(() => person.id),
  label: text("label"),
  lastSeen: text("last_seen"),
  trusted: integer("trusted", { mode: "boolean" }).notNull().default(false),
  ...timestamps,
});

export const breachFlag = sqliteTable("breach_flag", {
  id: key("breach_key", "brf"),
  personId: text("person_key").references(() => person.id),
  source: text("source"),
  detail: text("detail"),
  seenAt: text("seen_at"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Merges, audit, retention, vendors, flags
 * ------------------------------------------------------------------ */

export const mergeSuggestion = sqliteTable("merge_suggestion", {
  id: key("suggestion_key", "mgs"),
  primaryTicketId: text("primary_ticket_key").references(() => ticket.id),
  duplicateTicketId: text("duplicate_ticket_key").references(() => ticket.id),
  confidence: integer("confidence"),
  status: text("status").notNull().default("suggested"),
  ...timestamps,
});

export const mergeLog = sqliteTable("merge_log", {
  id: key("merge_key", "mgl"),
  primaryTicketId: text("primary_ticket_key").references(() => ticket.id),
  mergedTicketId: text("merged_ticket_key").references(() => ticket.id),
  actor: text("actor"),
  reversibleUntil: text("reversible_until"),
  reversedAt: text("reversed_at"),
  ...timestamps,
}, (t) => ({
  // The active-merge lookup (reversed_at IS NULL) done on the inbox path when
  // de-duplication is on, and by the merge candidate/list endpoints.
  byActive: index("idx_merge_log_active").on(t.reversedAt, t.mergedTicketId),
}));

export const auditEvent = sqliteTable(
  "audit_event",
  {
    id: key("audit_key", "aud"),
    actor: text("actor"),
    event: text("event").notNull(),
    targetKind: text("target_kind", { enum: AUDIT_TARGET_KINDS }),
    targetId: text("target_id"),
    mlsId: text("mls_id").references(() => tenant.id),
    ip: text("ip"),
    ua: text("ua"),
    before: text("before", { mode: "json" }).$type<Record<string, unknown> | null>(),
    after: text("after", { mode: "json" }).$type<Record<string, unknown> | null>(),
    ...timestamps,
  },
  (t) => ({
    byTenant: index("idx_audit_mls").on(t.mlsId),
    byTarget: index("idx_audit_target").on(t.targetKind, t.targetId),
  }),
);

export const retentionRule = sqliteTable("retention_rule", {
  id: key("rule_key", "ret"),
  mlsId: text("mls_id").references(() => tenant.id),
  entity: text("entity").notNull(),
  retainDays: integer("retain_days").notNull(),
  ...timestamps,
});

export const legalHold = sqliteTable("legal_hold", {
  id: key("hold_key", "lgh"),
  mlsId: text("mls_id")
    .notNull()
    .references(() => tenant.id),
  reason: text("reason"),
  active: integer("active", { mode: "boolean" }).notNull().default(true),
  ...timestamps,
});

export const dataExport = sqliteTable("data_export", {
  id: key("export_key", "exp"),
  mlsId: text("mls_id")
    .notNull()
    .references(() => tenant.id),
  requestedBy: text("requested_by").references(() => staffUser.id),
  r2Key: text("r2_key"),
  signedUrlExpiresAt: text("signed_url_expires_at"),
  status: text("status").notNull().default("pending"),
  ...timestamps,
});

export const vendor = sqliteTable("vendor", {
  id: key("vendor_key", "ven"),
  name: text("name").notNull(),
  category: text("category"),
  status: text("status").notNull().default("active"),
  ...timestamps,
});

export const featureFlag = sqliteTable("feature_flag", {
  id: key("flag_key", "flg"),
  slug: text("slug").notNull().unique(),
  enabled: integer("enabled", { mode: "boolean" }).notNull().default(false),
  description: text("description"),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * Knowledge content + content studio (v3 — DATA_MODEL.md §1)
 *
 * ONE store. A published help-centre article and a studio draft are the same
 * row at different `state` values. Provenance is `origin` + `client_id` (never a
 * flag on the side): it decides who can edit and who can read (DECISIONS.md §1).
 *
 * Adaptation from DATA_MODEL.md: `published_at`/`updated_at` are TEXT ISO here
 * (via the repo's `timestamps`), not INTEGER epoch — to match every other table
 * in this DB. The constraints are kept exactly; they are the load-bearing part.
 * ------------------------------------------------------------------ */

export const article = sqliteTable(
  "article",
  {
    id: key("article_key", "art"),
    slug: text("slug").notNull(),
    title: text("title").notNull(),
    excerpt: text("excerpt").notNull(),
    // Block JSON — array of typed tuples; see DATA_MODEL.md §1.2 (ten block kinds).
    body: text("body", { mode: "json" }).$type<unknown[]>().notNull(),
    kind: text("kind", { enum: ARTICLE_KINDS }).notNull().default("article"),
    category: text("category").notNull(),
    audience: text("audience", { enum: ARTICLE_AUDIENCES }).notNull(),
    plan: text("plan", { enum: ARTICLE_PLANS }).notNull().default("included"),
    // provenance ---------------------------------------------------------
    origin: text("origin", { enum: CONTENT_ORIGINS }).notNull(),
    // NULL brand = all brands (platform docs surfaced under any skin).
    brandId: text("brand_id", { enum: BRAND_IDS }),
    // REQUIRED when origin='tenant', NULL when 'platform' (enforced by check).
    clientId: text("client_id").references(() => tenant.id),
    // lifecycle ----------------------------------------------------------
    state: text("state", { enum: ARTICLE_STATES }).notNull(),
    version: integer("version").notNull().default(1),
    // Self-FK: a superseded row's successor must be a real article, not a dangling
    // id (DATA_MODEL.md §1 — the retire flow must not leave a broken banner link).
    supersededBy: text("superseded_by").references((): AnySQLiteColumn => article.id),
    publishedAt: text("published_at"),
    ownerAdminId: text("owner_admin_id").references(() => staffUser.id),
    ...timestamps,
  },
  (t) => ({
    // The read-rule index (DATA_MODEL.md §1): origin, brand, client, state.
    read: index("idx_article_read").on(t.origin, t.brandId, t.clientId, t.state),
    // The slug is the public lookup key and must resolve to exactly one article
    // within any single reader view. The portal shows ONE space at a time —
    // platform docs, or one client space — so uniqueness is scoped per space, and
    // both indexes are partial on `state='published'` (version history, e.g. a
    // superseded v1 beside a published v2 with the same slug, stays legal):
    //
    //  • Platform docs: a NULL-brand ("all brands") row is co-visible with every
    //    brand-specific row, so platform slugs are unique across ALL brands — one
    //    index on `slug` alone. (Two brands can't safely share a slug because the
    //    all-brands namespace overlaps both.)
    //  • Client space: unique per `(client_id, slug)`.
    publishedPlatformSlug: uniqueIndex("idx_article_published_platform_slug")
      .on(t.slug)
      .where(sql`state = 'published' and origin = 'platform'`),
    publishedTenantSlug: uniqueIndex("idx_article_published_tenant_slug")
      .on(t.clientId, t.slug)
      .where(sql`state = 'published' and origin = 'tenant'`),
    // origin='tenant' iff client_id set — the single line that keeps platform
    // pushes from ever touching a tenant row (DECISIONS.md §1 corollary).
    tenantHasClient: check(
      "chk_article_tenant_has_client",
      sql`(origin = 'tenant') = (client_id is not null)`,
    ),
    // A superseded row must name its successor.
    supersededHasSuccessor: check(
      "chk_article_superseded_successor",
      sql`state <> 'superseded' or superseded_by is not null`,
    ),
  }),
);

// The generation queue. A proposal is a unit of work the studio proposes; it
// resolves into an article row (or supersedes/retires one). DATA_MODEL.md §1.3.
export const contentProposal = sqliteTable(
  "content_proposal",
  {
    id: key("proposal_key", "cpr"),
    articleId: text("article_key").references(() => article.id), // NULL for kind='new'
    // The proposed article title — carried on the proposal so a kind='new' item
    // (no linked article yet) still shows its real title in the queue.
    title: text("title").notNull().default(""),
    kind: text("kind", { enum: PROPOSAL_KINDS }).notNull(),
    state: text("state", { enum: PROPOSAL_STATES }).notNull(),
    source: text("source", { enum: CONTENT_SOURCES }).notNull(),
    triggerRef: text("trigger_ref").notNull(), // release id, field name, cluster id…
    triggerLabel: text("trigger_label").notNull(), // human string shown in the queue
    rationale: text("rationale").notNull(), // the "why"; always shown to the reviewer
    confidence: integer("confidence").notNull(), // 0–100
    // JSON [["add"|"cut"|"keep", "text"], …]
    diff: text("diff", { mode: "json" }).$type<[string, string][]>().notNull(),
    // JSON [[kind, what, note], …] — media implications, incl. invalidated chapters
    media: text("media", { mode: "json" }).$type<[string, string, string][]>(),
    scopeBrandId: text("scope_brand_id", { enum: BRAND_IDS }),
    scopeClientId: text("scope_client_id").references(() => tenant.id),
    reviewerId: text("reviewer_id").references(() => staffUser.id),
    // Set by policy evaluation server-side, NEVER supplied by the generator
    // (DECISIONS.md §8 / autoPublishable in content.ts).
    autoPublishable: integer("auto_publishable", { mode: "boolean" }).notNull().default(false),
    resolution: text("resolution", { enum: PROPOSAL_RESOLUTIONS }),
    // Required when resolution='rejected' — the pipeline's only training signal
    // (DECISIONS.md §9).
    rejectionReason: text("rejection_reason"),
    resolvedAt: text("resolved_at"),
    // A per-publish random token the publish pipeline writes on the CLAIM and the
    // coupled article write checks — proving the write belongs to THIS request's
    // claim (not a prior publish of the same proposal, and not a same-millisecond
    // race). See publishProposal in apps/api/src/content-gen.ts.
    publishToken: text("publish_token"),
    ...timestamps,
  },
  (t) => ({
    byState: index("idx_content_proposal_state").on(t.state, t.createdAt),
    // One proposal per trigger — the DB backstop against duplicate generation
    // (a concurrent generate run can't create a second proposal for the same
    // release/field/cluster ref).
    uniqTrigger: uniqueIndex("idx_content_proposal_trigger").on(t.triggerRef),
    // A rejected proposal must carry a reason (DECISIONS.md §9).
    rejectionHasReason: check(
      "chk_proposal_rejection_reason",
      sql`resolution <> 'rejected' or rejection_reason is not null`,
    ),
    // Only kind='new' may omit the target article (DATA_MODEL.md §1.3 — article_id
    // is "NULL for kind='new'"). A `new` article OR a `video` (which produces a
    // brand-new video article — V3_README §9) may omit the target; `update` and
    // `retire` must point at a real article, or the queue holds a proposal with
    // nothing to act on.
    nonNewHasArticle: check(
      "chk_proposal_non_new_has_article",
      sql`kind in ('new', 'video') or article_key is not null`,
    ),
  }),
);

// Video-pipeline production rows (V3_README §9 / DECISIONS.md Q6). A video (or
// media-bearing) proposal has one row per artifact — storyboard, voiceover,
// captions, screenshot, the video itself — each with a REAL state so the studio
// can gate publish on human review ("a person watches it first") and the pipeline
// can drive transitions. Videos remain ARTICLES (kind='video'); this table holds
// only the production state, never a parallel video store.
export const contentMedia = sqliteTable(
  "content_media",
  {
    id: key("media_key", "cmd"),
    proposalId: text("proposal_key").notNull().references(() => contentProposal.id),
    kind: text("kind", { enum: MEDIA_KINDS }).notNull(),
    label: text("label").notNull(), // the "what" string shown in the queue
    note: text("note"), // free text — affected chapter, language list, etc.
    state: text("state", { enum: MEDIA_STATES }).notNull().default("pending"),
    // The produced artifact's real asset reference (the uploaded clip / drop-zone
    // id), set by whatever produces it (Q6 — internal tool / vendor / human). NULL
    // until produced. A video may publish only when its `video` row is `ready` AND
    // carries an asset_ref: that ref — never a fabricated id — is the article's
    // `vid` asset, so a state row is never published as if it were the asset.
    assetRef: text("asset_ref"),
    // The Cloudflare Stream UID this row's direct-upload was MINTED for, set
    // server-side when the upload is created. The status poll confirms/promotes
    // THIS uid to asset_ref — the client never supplies which uid to attach, and a
    // slow encode is resumable (the pending uid survives). Cleared on promotion.
    pendingAssetRef: text("pending_asset_ref"),
    // The video chapter a change invalidated (e.g. "11:40"), when state='needs_rerecord'.
    chapterRef: text("chapter_ref"),
    ...timestamps,
  },
  (t) => ({
    byProposal: index("idx_content_media_proposal").on(t.proposalId),
  }),
);

// Which of the six watched sources are live. Seeded with all six enabled; the
// studio's policy table reads this (V3_README §C, DATA_MODEL.md §1.3).
export const contentSourceConfig = sqliteTable("content_source_config", {
  source: text("source", { enum: CONTENT_SOURCES }).primaryKey(),
  enabled: integer("enabled", { mode: "boolean" }).notNull().default(true),
  ...timestamps,
});

/* ------------------------------------------------------------------ *
 * RealtyID ↔ Displet identity bridge (v3 — DATA_MODEL.md §5)
 *
 * The RID is the permanent key and follows the person between brokerages and
 * boards; the Displet user id does not. When someone changes brokerage the link
 * survives and their memberships change underneath it. Needed by the portal's
 * RealtyID sign-in (DECISIONS.md §2). `user_id` maps to this repo's `person`.
 * ------------------------------------------------------------------ */
export const realtyIdLink = sqliteTable(
  "realty_id_link",
  {
    rid: text("rid").primaryKey(), // permanent RealtyID
    userId: text("user_key")
      .notNull()
      .references(() => person.id),
    verifiedVia: text("verified_via", { enum: REALTY_ID_VERIFY_VIA }).notNull(),
    ial: integer("ial"), // identity assurance level, from RealtyID
    linkedAt: text("linked_at").notNull(),
    ...timestamps,
  },
  // One canonical RID per person: sign-in resolution needs a single row, and a
  // person's permanent RealtyID is 1:1 with them (the RID is the stable key; the
  // Displet user_id changes under it). Unique on user_key enforces that.
  (t) => ({ byUser: uniqueIndex("idx_realty_id_link_user").on(t.userId) }),
);

// Outbox for DEFERRED R2 raw-email deletion. ONE row per ticket (id is derived from
// the ticket key), carrying ALL of that ticket's raw .eml keys as a JSON array —
// so scheduling is a SINGLE upsert that commits ATOMICALLY inside the ticket's
// status-change transaction (no batch-size limit regardless of thread length, and
// no hide-vs-restore race: status and schedule move together). Marking spam/deleted
// upserts the row with deleteAfter = 90 days out; restoring deletes it. It lives
// OUTSIDE the ticket (ticketKey is a plain value, no FK) so the schedule survives
// the ticket's own 30-day hard-purge and still fires at day 90; an hourly cron
// drains rows whose deleteAfter has passed.
export const emailRawDelete = sqliteTable(
  "email_raw_delete",
  {
    id: key("raw_delete_key", "erd"),
    ticketKey: text("ticket_key").notNull(), // NOT an FK — the ticket may be purged before this fires
    mlsId: text("mls_id"), // tenant, captured at schedule time so the drain can honor legal holds (null = external, no hold)
    r2Keys: text("r2_keys", { mode: "json" }).$type<string[]>().notNull(), // all raw .eml keys for the ticket
    deleteAfter: text("delete_after").notNull(), // ISO; the drain deletes once now >= this
    ...timestamps,
  },
  (t) => ({
    byDue: index("idx_email_raw_delete_due").on(t.deleteAfter),
    byTicket: index("idx_email_raw_delete_ticket").on(t.ticketKey),
  }),
);
